import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, Navigate } from 'react-router-dom';
import { Brain, RefreshCw } from 'lucide-react';
import useAuthStore from '../store/authStore';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { register as registerUser, resendVerification } from '../services/api/auth';
import type { RegisterData } from '../types';
import { countries } from '../data/countries';

const registerSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  surname: z.string().min(2, 'Surname must be at least 2 characters'),
  email: z.string().email('Invalid email address'),
  organization: z.string().min(2, 'Organization must be at least 2 characters'),
  country: z.string().min(2, 'Country is required'),
  companyRegistrationNumber: z.string().optional(),
  password: z.string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[0-9]/, 'Password must contain at least one number')
    .regex(/[!@#$%^&*]/, 'Password must contain at least one special character'),
  confirmPassword: z.string()
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type RegisterForm = z.infer<typeof registerSchema>;

interface VerificationPendingProps {
  email: string;
  onResendVerification: () => void;
  isResending: boolean;
  resendError: string;
}

const VerificationPending: React.FC<VerificationPendingProps> = ({
  email,
  onResendVerification,
  isResending,
  resendError
}) => (
  <div className="min-h-screen bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center p-4">
    <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8 text-center">
      <Brain className="h-12 w-12 text-cyan-600 mx-auto mb-4" />
      <h1 className="text-2xl font-bold text-gray-900 mb-4">Verify Your Email</h1>
      <p className="text-gray-600 mb-6">
        We've sent a verification link to:
        <br />
        <span className="font-medium">{email}</span>
      </p>
      {resendError && (
        <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm">
          {resendError}
        </div>
      )}
      <button
        onClick={onResendVerification}
        disabled={isResending}
        className="flex items-center justify-center space-x-2 w-full py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 disabled:opacity-50"
      >
        {isResending ? (
          <>
            <RefreshCw className="h-5 w-5 animate-spin" />
            <span>Resending...</span>
          </>
        ) : (
          <>
            <RefreshCw className="h-5 w-5" />
            <span>Resend Verification Email</span>
          </>
        )}
      </button>
      <p className="mt-6 text-sm text-gray-500">
        Already verified? <Link to="/login" className="text-cyan-600 hover:text-cyan-500">Sign in</Link>
      </p>
    </div>
  </div>
);

const Register: React.FC = () => {
  const { isAuthenticated } = useAuthStore();
  const [registrationStatus, setRegistrationStatus] = useState<'idle' | 'loading' | 'success' | 'error' | 'verification_pending'>('idle');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [registeredEmail, setRegisteredEmail] = useState<string>('');
  const [isResending, setIsResending] = useState(false);
  const [resendError, setResendError] = useState<string>('');
  
  const { register, handleSubmit, formState: { errors }, getValues } = useForm<RegisterForm>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      country: 'South Africa'
    }
  });

  if (isAuthenticated) {
    return <Navigate to="/dashboard" />;
  }

  const handleResendVerification = async () => {
    const emailToResend = registeredEmail || getValues('email');
    if (!emailToResend) return;

    try {
      setIsResending(true);
      setResendError('');
      await resendVerification(emailToResend);
      setResendError('');
    } catch (error) {
      console.error('Resend verification error:', error);
      setResendError(error instanceof Error ? error.message : 'Failed to resend verification email');
    } finally {
      setIsResending(false);
    }
  };

  const onSubmit = async (data: RegisterForm) => {
    try {
      setRegistrationStatus('loading');
      setErrorMessage('');
      setRegisteredEmail(data.email);

      const registrationData: RegisterData = {
        name: data.name,
        surname: data.surname,
        email: data.email,
        organization: data.organization,
        country: data.country,
        companyRegistrationNumber: data.companyRegistrationNumber,
        password: data.password
      };

      await registerUser(registrationData);
      setRegistrationStatus('success');
    } catch (error) {
      console.error('Registration error:', error);
      
      if (error instanceof Error && 
          (error.message.includes('pending verification') || 
           error.message.includes('verification email was already sent'))) {
        setRegisteredEmail(data.email);
        setRegistrationStatus('verification_pending');
      } else {
        setRegistrationStatus('error');
        setErrorMessage(error instanceof Error ? error.message : 'Registration failed. Please try again.');
      }
    }
  };

  if (registrationStatus === 'success' || registrationStatus === 'verification_pending') {
    return (
      <VerificationPending
        email={registeredEmail}
        onResendVerification={handleResendVerification}
        isResending={isResending}
        resendError={resendError}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8">
        <div className="flex flex-col items-center mb-8">
          <Brain className="h-12 w-12 text-cyan-600 mb-4" />
          <h1 className="text-2xl font-bold text-gray-900">Create an Account</h1>
          <p className="text-gray-500 mt-2 text-center">
            Join Prestige AI and access powerful AI tools
          </p>
        </div>

        {errorMessage && (
          <div className="mb-6 p-4 bg-red-50 text-red-600 rounded-lg text-sm">
            {errorMessage}
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                Name
              </label>
              <input
                {...register('name')}
                className="mt-1 block w-full rounded-lg"
                placeholder="John"
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="surname" className="block text-sm font-medium text-gray-700">
                Surname
              </label>
              <input
                {...register('surname')}
                className="mt-1 block w-full rounded-lg"
                placeholder="Doe"
              />
              {errors.surname && (
                <p className="mt-1 text-sm text-red-600">{errors.surname.message}</p>
              )}
            </div>
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
              Email
            </label>
            <input
              {...register('email')}
              type="email"
              className="mt-1 block w-full rounded-lg"
              placeholder="john@example.com"
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="organization" className="block text-sm font-medium text-gray-700">
              Organization
            </label>
            <input
              {...register('organization')}
              className="mt-1 block w-full rounded-lg"
              placeholder="Company Name"
            />
            {errors.organization && (
              <p className="mt-1 text-sm text-red-600">{errors.organization.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="country" className="block text-sm font-medium text-gray-700">
              Country
            </label>
            <select
              {...register('country')}
              className="mt-1 block w-full rounded-lg border-gray-300 focus:border-cyan-500 focus:ring-cyan-500"
            >
              {countries.map((country) => (
                <option key={country} value={country}>
                  {country}
                </option>
              ))}
            </select>
            {errors.country && (
              <p className="mt-1 text-sm text-red-600">{errors.country.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="companyRegistrationNumber" className="block text-sm font-medium text-gray-700">
              Company Registration Number (Optional)
            </label>
            <input
              {...register('companyRegistrationNumber')}
              className="mt-1 block w-full rounded-lg"
              placeholder="Registration Number"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700">
              Password
            </label>
            <input
              {...register('password')}
              type="password"
              className="mt-1 block w-full rounded-lg"
              placeholder="••••••••"
            />
            {errors.password && (
              <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
              Confirm Password
            </label>
            <input
              {...register('confirmPassword')}
              type="password"
              className="mt-1 block w-full rounded-lg"
              placeholder="••••••••"
            />
            {errors.confirmPassword && (
              <p className="mt-1 text-sm text-red-600">{errors.confirmPassword.message}</p>
            )}
          </div>

          <button
            type="submit"
            disabled={registrationStatus === 'loading'}
            className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 disabled:opacity-50"
          >
            {registrationStatus === 'loading' ? 'Creating Account...' : 'Create Account'}
          </button>
        </form>

        <p className="mt-6 text-center text-sm text-gray-500">
          Already have an account?{' '}
          <Link to="/login" className="text-cyan-600 hover:text-cyan-500">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Register;